import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


export const exportKnoxTableToExcel = async (knoxData, periodData) => {
  try {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Knock-Off Table');

    // Set worksheet properties
    worksheet.properties.defaultRowHeight = 20;

    // Define column headers (matching your table)
    const columns = [
      { header: 'S.No', key: 'sno', width: 8 },
      { header: 'IRM Reference', key: 'irmNumber', width: 20 },
      { header: 'IRM Date', key: 'irmDate', width: 15 },
      { header: 'Available IRM Balance', key: 'availableIRMBalance', width: 22 },
      { header: 'Remitter Name', key: 'remitterName', width: 25 },
      { header: 'AD Code', key: 'adCode', width: 15 },
      { header: 'Shipping Bill Number', key: 'shippingBillNumber', width: 22 },
      { header: 'Available SB Balance', key: 'availableSBBalance', width: 22 },
      { header: 'Shipping Bill Date', key: 'shippingBillDate', width: 18 },
      { header: 'Invoice Number', key: 'invoiceNumber', width: 20 },
      { header: 'Invoice Date', key: 'invoiceDate', width: 15 },
      { header: 'FOB Value', key: 'fobValue', width: 18 },
      { header: 'Consignee Name', key: 'consigneeName', width: 25 },
      { header: 'Buyer Name', key: 'buyerName', width: 25 },
      { header: 'GST Number', key: 'gstNumber', width: 20 },
      { header: 'Currency', key: 'currency', width: 12 },
      { header: 'Port Code', key: 'portCode', width: 12 },
      { header: 'SB AD Code', key: 'sbAdCode', width: 15 },
      { header: 'Adjusted Amount', key: 'adjustedAmount', width: 18 },
      { header: 'Pending Balance in IRM', key: 'pendingBalanceInIRM', width: 22 },
      { header: 'SB Balance', key: 'sbBalance', width: 18 },
      { header: 'BRC Number', key: 'brcNumber', width: 20 },
      { header: 'BRC Date', key: 'brcDate', width: 15 },
      { header: 'Certificate Status', key: 'certificateStatus', width: 18 },
      { header: 'Remarks', key: 'remarks', width: 50 }
    ];

    worksheet.columns = columns;

    // Add title row (merged cells)
    worksheet.mergeCells('A1:Y1');
    const titleRow = worksheet.getCell('A1');
    titleRow.value = 'Knock-Off Table - IRM to Shipping Bill Mapping';
    titleRow.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
    titleRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4472C4' }
    };
    titleRow.alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getRow(1).height = 30;

    // Add period info row
    if (periodData) {
      worksheet.mergeCells('A2:Y2');
      const periodRow = worksheet.getCell('A2');
      periodRow.value = `Period: ${periodData.start_date || 'N/A'} to ${periodData.end_date || 'N/A'}`;
      periodRow.font = { size: 12, bold: true };
      periodRow.alignment = { vertical: 'middle', horizontal: 'center' };
      periodRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE7E6E6' }
      };
      worksheet.getRow(2).height = 25;
    }

    // Add header row (row 3)
    const headerRow = worksheet.getRow(3);
    headerRow.values = columns.map(col => col.header);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF5B9BD5' }
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRow.height = 25;

    // Add borders to header
    headerRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Helper function to format date
    const formatDate = (dateValue) => {
      if (!dateValue) return 'N/A';
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toISOString().split('T')[0];
    };

    // Helper function to format currency
    const formatCurrency = (value, currency) => {
      if (!value || value === 0) return 0;
      return `${parseFloat(value).toLocaleString()} ${currency || 'USD'}`;
    };

    // Add data rows
    knoxData.forEach((row, index) => {
      const dataRow = worksheet.addRow({
        sno: index + 1,
        irmNumber: row.irmNumber || 'N/A',
        irmDate: formatDate(row.irmDate),
        availableIRMBalance: formatCurrency(row.availableIRMBalance, row.currency),
        remitterName: row.remitterName || 'N/A',
        adCode: row.adCode || 'N/A',
        shippingBillNumber: row.shippingBillNumber || 'N/A',
        availableSBBalance: formatCurrency(row.availableSBBalance, row.currency),
        shippingBillDate: formatDate(row.shippingBillDate),
        invoiceNumber: row.invoiceNumber || 'N/A',
        invoiceDate: formatDate(row.invoiceDate),
        fobValue: formatCurrency(row.fobValue, row.currency),
        consigneeName: row.consigneeName || 'N/A',
        buyerName: row.buyerName || 'N/A',
        gstNumber: row.gstNumber || 'N/A',
        currency: row.currency || 'N/A',
        portCode: row.portCode || 'N/A',
        sbAdCode: row.sbAdCode || 'N/A',
        adjustedAmount: formatCurrency(row.adjustedAmount, row.currency),
        pendingBalanceInIRM: formatCurrency(row.pendingBalanceInIRM, row.currency),
        sbBalance: formatCurrency(row.sbBalance, row.currency),
        brcNumber: row.brcNumber || 'N/A',
        brcDate: formatDate(row.brcDate),
        certificateStatus: row.certificateUploadStatus || 'Pending',
        remarks: row.remarks || 'N/A'
      });

      // Add borders to each cell
      dataRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      });

      // Center align S.No
      dataRow.getCell('sno').alignment = { vertical: 'middle', horizontal: 'center' };

      // Highlight rows with BRC generated
      if (row.brcGenerated === 1) {
        dataRow.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' }
          };
        });
      }

      // Highlight certificate status
      const certCell = dataRow.getCell('certificateStatus');
      if (row.certificateUploadStatus === 'success') {
        certCell.font = { color: { argb: 'FF28A745' }, bold: true };
      } else if (row.certificateUploadStatus === 'failed') {
        certCell.font = { color: { argb: 'FFDC3545' }, bold: true };
      } else {
        certCell.font = { color: { argb: 'FFFFC107' }, bold: true };
      }
    });

    // Auto-filter
    worksheet.autoFilter = {
      from: 'A3',
      to: `Y${worksheet.rowCount}`
    };

    // Freeze header rows
    worksheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 3 }
    ];

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Generate filename with timestamp
    const filename = `${periodData?.client_name || 'Unknown'}_Knox_Table_${periodData?.start_date || 'Unknown'}_to_${periodData?.end_date || 'Unknown'}.xlsx`;

    // Download file
    saveAs(blob, filename);

    return { success: true, message: 'Excel file downloaded successfully' };
  } catch (error) {
    console.error('Error generating Excel:', error);
    return { success: false, message: `Failed to generate Excel: ${error.message}` };
  }
};


export const getStatusDisplay = (status) => {
    const statusMap = {
        1: {
            label: "Success",
            className: "bg-green-100 text-green-700 px-3 py-1 rounded-full font-bold"
        },
        2: {
            label: "Processing",
            className: "bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-bold"
        },
        3: {
            label: "Failed",
            className: "bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold"
        },
    };

    const statusInfo = statusMap[status];
    return statusInfo ? (
        <span className={statusInfo.className}>{statusInfo.label}</span>
    ) : (
        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-sm font-bold">
            Unknown
        </span>
    );
};


